import { FormCheckbox, TextArea } from "@jhool-io/fe-components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
    ICancelSession,
    ICancelSessionForm,
} from "../../../../types/schedule.types";
import useToast from "../../../../../../hooks/useToast";
import { useCancelSession } from "../../../../../tasks/hooks/tasks.mutations";
import { useFetchUserDetails } from "../../../../../../hooks/queries/user";
import { useTerminateIntakeAppointment } from "../../../../hooks/schedule.mutations";

interface CancelSessionProps {
    onFormSubmit: () => void;
    shouldNavigate: boolean;
    sessionHistoryId: string | null;
    externalSessionId?: string | null;
    intakeClientId?: string | null;
    action: "cancel" | "terminate";
}

export default function CancelSessionForm({
    onFormSubmit,
    shouldNavigate,
    sessionHistoryId,
    externalSessionId,
    intakeClientId,
    action,
}: CancelSessionProps) {
    // Query client
    const queryClient = useQueryClient();
    const cancelSession = useCancelSession();
    const terminateIntakeAppointment = useTerminateIntakeAppointment();
    const { toast } = useToast();
    const loggedInUser = useFetchUserDetails();
    const navigate = useNavigate();

    const {
        register,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm<ICancelSessionForm>({
        mode: "onChange",
        resolver: yupResolver(
            yup.object().shape({
                notes: yup.string().when([], {
                    is: () => action === "terminate",
                    then: yup
                        .string()
                        .required(
                            "Please explain the reason for termination in sufficient detail"
                        )
                        .min(
                            50,
                            "Reason for termination must be at least 50 characters"
                        ),
                    otherwise: yup.string(),
                }),
                termination_message_requested: yup.bool(),
                reason_for_termination_msg_skip: yup
                    .string()
                    .when(
                        "termination_message_requested",
                        (termination_message_requested, field) =>
                            termination_message_requested === false
                                ? field.required(
                                      "Provide a reason why you want to skip notification"
                                  )
                                : field
                    ),
            })
        ),
    });

    const isMessageRequested = watch("termination_message_requested");

    const handleCancelSession = (data: ICancelSessionForm) => {
        if (!sessionHistoryId) {
            toast({
                mode: "error",
                message: "Could not cancel session at this time",
            });
        } else {
            const payload = {
                session_history_ids: [sessionHistoryId],
                reason: action === "terminate" ? "termination" : "cancelled",
            };
            const payloadToSend: ICancelSession = {
                ...data,
                reason: "Termination",
                caller_role: "admin",
                updated_by: loggedInUser?.data?.user_email as string,
            };
            const cancelProviderSession = () =>
                cancelSession.mutate(payload, {
                    onSuccess: async () => {
                        queryClient.invalidateQueries({
                            queryKey: [`todos`],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["calendar"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: [`caseload`],
                        });
                        queryClient.invalidateQueries({
                            queryKey: [`weekly-schedule`],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["provider-sessions"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["provider-upcoming-sessions"],
                        });
                        toast({
                            mode: "success",
                            message: `${
                                action === "terminate"
                                    ? "Session terminated successfully!"
                                    : "Session cancelled successfully!"
                            }`,
                        });
                        if (shouldNavigate) {
                            navigate(`/add-note?note_type=cancellation_note`);
                        }
                        if (shouldNavigate && action === "terminate") {
                            navigate(
                                `/add-note?note_type=termination_note&session_history_id=${sessionHistoryId}`
                            );
                        }
                        onFormSubmit();
                    },
                    onError: () => {
                        toast({
                            mode: "error",
                            message: "Could not cancel session",
                        });
                    },
                });

            if (action === "terminate") {
                if (!intakeClientId || !externalSessionId) {
                    toast({
                        mode: "error",
                        message: "Could not terminate session at this time",
                    });
                    return;
                }

                terminateIntakeAppointment.mutate(
                    {
                        intakeClientId,
                        externalSessionId,
                        payload: payloadToSend,
                    },
                    {
                        onSuccess: () => {
                            cancelProviderSession();
                        },
                        onError: () => {
                            toast({
                                mode: "error",
                                message:
                                    "Could not terminate session at this time",
                            });
                        },
                    }
                );
                return;
            }

            cancelProviderSession();
        }
    };

    return (
        <form
            id="cancel_client_session"
            name="cancel_client_session"
            aria-label="cancel_client_session"
            onSubmit={handleSubmit(handleCancelSession)}
        >
            <div>
                {action === "cancel" ? (
                    <h3 className="text-title font-semibold text-text mb-2">
                        Report session as Cancelled
                    </h3>
                ) : null}
                {action === "terminate" ? (
                    <p className="text-base font-normal text-textSec">
                        Terminating this client is permanent.
                    </p>
                ) : (
                    <p className="text-base font-normal text-textSec">
                        Did the client cancel the session at least 24 hours
                        before the start time
                    </p>
                )}
            </div>

            {action === "terminate" && (
                <div className="my-[--spacing-form-items]">
                    <div className="fg">
                        <TextArea
                            {...register("notes")}
                            label="Reasons for Termination"
                            placeholder="Please explain the reason for termination in sufficient detail"
                            hasError={!!errors?.notes}
                            errorText={errors?.notes?.message}
                        />
                    </div>
                    <div className="fg">
                        <FormCheckbox
                            {...register("termination_message_requested")}
                            label="Notify the client of the termination"
                            defaultChecked
                            labelClass="text-sm font-normal"
                        />
                    </div>
                </div>
            )}

            {action === "terminate" && isMessageRequested === false && (
                <div className="fg">
                    <TextArea
                        {...register("reason_for_termination_msg_skip")}
                        label="Provide a reason for not notifying the client"
                        placeholder="Enter a reason"
                        hasError={!!errors?.reason_for_termination_msg_skip}
                        errorText={
                            errors?.reason_for_termination_msg_skip?.message
                        }
                    />
                </div>
            )}
        </form>
    );
}
